import { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { displayRange, parseDisplay, toDisplay, unitLabel } from '../format';
import type { ResolvedSetting } from '../../types';
import type { ControlProps } from './types';

/**
 * The text of a number field, kept apart from the value: "0." or "" are on
 * their way to a number, and turning them back into one at every keystroke
 * would fight the person typing. The text follows the value only when the
 * value changes from outside (a reset, a prefill).
 */
export function useNumberText(setting: ResolvedSetting, value: unknown): [string, (text: string) => number | undefined] {
  const shown = typeof value === 'number' && Number.isFinite(value) ? String(toDisplay(setting, value)) : '';
  const [text, setText] = useState(shown);
  useEffect(() => {
    if (parseDisplay(setting, text) !== value) setText(shown);
    // Only when the value moves: `text` is the person's.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown]);
  const change = (next: string) => {
    setText(next);
    return parseDisplay(setting, next);
  };
  return [text, change];
}

/**
 * A number typed in, with its unit after it. A ratio with the unit "%" is
 * typed as a percentage. On its own it fills the control column like every
 * other control; `compact` is the box beside a slider or in a range ("from
 * … to …"), a few digits wide.
 */
export function NumberControl({
  id,
  setting,
  value,
  onChange,
  disabled,
  invalid,
  describedBy,
  compact = false,
  className,
}: ControlProps & { compact?: boolean; className?: string }) {
  const [text, change] = useNumberText(setting, value);
  const { min, max } = displayRange(setting);
  const unit = unitLabel(setting);
  return (
    <div className={cn('flex items-center gap-2', !compact && 'w-full', className)}>
      <Input
        id={id}
        type="number"
        inputMode="decimal"
        step="any"
        min={min}
        max={max}
        value={text}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        onChange={(e) => onChange(change(e.target.value))}
        className={cn('h-8 text-right tabular-nums', compact ? 'w-24' : 'min-w-0 flex-1')}
      />
      {unit ? <span className="shrink-0 text-xs text-muted-foreground">{unit}</span> : null}
    </div>
  );
}
